import { useIoContext, useStoreActions, useStoreStatic } from '~/contexts';
import { handlePromise, wait } from '~/infra/utils';
import { useOnMount } from './useOnMount';
import { buildIdentityMismatchReloadLog, hasRestoredIdentityMismatch, isReloadAttemptThrottled, markReloadAttempt } from './useRestoredDocumentIdentityGuard.utils';
const LOG_FLUSH_TIMEOUT_MS = 400;
// WHY: a bfcache/frozen-tab restore resumes a pre-swap identity while the cookie may have been re-scoped (ANCR-4460)
export function useRestoredDocumentIdentityGuard() {
    const { cookies, browser, sessionStorage, getNow } = useIoContext();
    const { getState } = useStoreStatic();
    const { api } = useStoreActions();
    useOnMount(()=>{
        let reloadStarted = false;
        function reloadIfIdentityMismatch(trigger) {
            if (reloadStarted) return;
            const cookieClaims = cookies.getClaims();
            const storeClaims = getState().claims;
            if (cookieClaims == null || storeClaims == null) return;
            if (storeClaims.setPasswordOnly || storeClaims.setIdentityOnly) return;
            if (getState().isSwitchingBusiness) return;
            if (!hasRestoredIdentityMismatch(cookieClaims, storeClaims)) return;
            const nowMs = getNow().getTime();
            if (isReloadAttemptThrottled(sessionStorage, nowMs, cookieClaims.businessId)) return;
            reloadStarted = true;
            markReloadAttempt(sessionStorage, nowMs, cookieClaims.businessId);
            const reportDelivery = handlePromise(api.frontendLogging.log(buildIdentityMismatchReloadLog({
                trigger,
                cookieClaims,
                storeClaims,
                pathname: browser.getPathname()
            })));
            void Promise.race([
                reportDelivery,
                wait(LOG_FLUSH_TIMEOUT_MS)
            ]).then(()=>browser.locationReload());
        }
        const unregisterPageRestored = browser.registerPageRestoredListener(()=>reloadIfIdentityMismatch('pageRestored'));
        const unregisterBecameVisible = browser.registerBecameVisibleListener(()=>reloadIfIdentityMismatch('becameVisible'));
        return ()=>{
            unregisterPageRestored();
            unregisterBecameVisible();
        };
    });
}
